/**
 * @fileoverview WEB-806 — self-gated event info blocks with source-accurate headings.
 *
 * Not an `EventModuleKind`. Adding this copy to the lexicon module order would
 * force an empty heading (or a WEB-719 degrade note) onto events that have no
 * info fields. The spine mounts this molecule after the figure and before the
 * numbered modules so operational copy can appear without occupying a slot.
 *
 * Server-safe: no hooks, no `'use client'`. Source copy is a React text node
 * (escaped) — never `dangerouslySetInnerHTML`. Long bodies wrap; they are
 * not line-clamped.
 */

import type { ReactElement } from 'react';

import {
  resolveEventSourceInfoBlocks,
  type EventSourceInfoBlock,
} from '@/lib/eventDetail/eventSourceCopy';
import type { EventDetailV2 } from '@/types/eventDetail';

export interface EventSourceInfoBlocksProps {
  event: EventDetailV2;
}

interface SourceInfoBlockProps {
  block: EventSourceInfoBlock;
}

function SourceInfoBlock({ block }: SourceInfoBlockProps): ReactElement {
  return (
    <section
      className="min-w-0 overflow-x-hidden border-t border-ds-border pt-(--evt-sgap)"
      data-testid="event-source-info-block"
      data-info-kind={block.kind}
    >
      <h2
        className="m-0 min-w-0 wrap-break-word font-semibold tracking-[-0.01em] leading-[1.45] text-fg-1 font-display mb-4 text-(length:--evt-sec)"
        data-testid="event-source-info-heading"
      >
        {block.heading}
      </h2>
      <p
        className="m-0 max-w-[60ch] wrap-break-word whitespace-pre-wrap leading-relaxed text-fg-2"
        data-testid="event-source-info-body"
      >
        {block.body}
      </p>
    </section>
  );
}

/**
 * Source-accurate info blocks. Returns `null` when every field is absent so
 * the spine never mounts an empty wrapper.
 */
export function EventSourceInfoBlocks({ event }: EventSourceInfoBlocksProps): ReactElement | null {
  const blocks = resolveEventSourceInfoBlocks(event);
  if (blocks.length === 0) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-col" data-testid="event-source-info">
      {blocks.map((block) => (
        <SourceInfoBlock key={block.kind} block={block} />
      ))}
    </div>
  );
}
